import Card, { CardProps } from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import { ApexOptions } from 'apexcharts';
import { useTheme, alpha as hexAlpha } from '@mui/material/styles';
import { Chart, ChartOptions, useChart } from 'src/components/chart';
import { fShortenAndRoundNumber, fShortenNumber } from 'src/utils/format-number';
import { f } from 'src/utils/format';
import moment from 'moment';
import { scaleSymlog } from 'd3-scale';
import { useEffect, useState } from 'react';
import { Box, Button, FormControlLabel, Stack, Switch } from '@mui/material';
import { useTranslate } from 'react-admin';

type Props = CardProps & {
  titleRevenue?: string;
  titleProfit?: string;
  tooltipRevenue?: string;
  tooltipProfit?: string;
  subheader?: string;
  chart: {
    colors?: string[];
    categories?: string[];
    series: {
      name?: string;
      data: number[];
    }[];
  };
};

export function TopPnlChart({ title, subheader, chart, ...other }: Props) {
  const theme = useTheme();
  const [showLogScale, setShowLogScale] = useState(false);
  const translate = useTranslate();

  const toggleLabels = ['1 D', '1 W', '1 M'];
  const m_categories = chart?.categories || [];

  const symlog = scaleSymlog().domain([0.01, 1000]).range([0, 100]);

  const transformedSeries = chart.series.map((serie) => ({
    ...serie,
    data: serie.data.map((value) => symlog(value)),
  }));

  const handleToggleProfit = () => {
    setShowLogScale((prev) => !prev);
  };
  // const handleToggle = (index: number) => {
  //   setActiveIndex((prevIndex) => (prevIndex === index ? null : index));
  // };

  const maxDataValue = Math.max(...chart.series.flatMap((serie) => serie.data));
  const maxSymlogValue = symlog(maxDataValue);

  const filteredSeries = showLogScale ? transformedSeries : chart.series;

  const logScaleChartOptions = useChart({
    colors: ['#2CA04E', '#E4B200', '#40A8D6', '#D9534F', '#D8BFD8'],
    legend: { show: true, showForSingleSeries: true },
    xaxis: {
      categories: chart.categories,
      labels: {
        formatter: (value, timestamp: number, opts: number) => {
          if (m_categories.length > 30) {
            const index = (chart.categories || []).indexOf(value);
            return index % 7 === 0 ? moment(new Date(value).toString()).format('DD-MM-YY') : '';
          }
          return moment(new Date(value).toString()).format('DD-MM-YY');
        },
        rotate: -45,
        rotateAlways: true,
      },
    },
    yaxis: [
      {
        labels: {
          formatter: (value: number) => fShortenAndRoundNumber(Math.round(symlog.invert(value))),
        },
        min: (min) => {
          if (min >= 0) return 0;
          // eslint-disable-next-line prefer-exponentiation-operator, no-restricted-properties
          const magnitude = Math.pow(10, Math.floor(Math.log10(-min)));
          const multiple = -min / magnitude; // example : 1.22
          const offset = multiple - Math.floor(multiple); // example : 0.22
          const buffer = offset < 0.5 ? 0.5 : 0;
          const bufferMagnitude = buffer * magnitude;
          const result = -(Math.round(multiple) * magnitude + bufferMagnitude);
          return result;
        },
        max: maxSymlogValue,
      },
    ],
    tooltip: {
      y: {
        // formatter: (value: number) => f(symlog.invert(value), { decimal: 2 }) + '(USD)',
        // title: {
        //   formatter: (seriesName: string) => {
        //     return seriesName.replace(/\s*\(.*?\)\s*/g, '');
        //   },
        // },
        formatter: (value: number, { series, seriesIndex, dataPointIndex }) => {
          const allValues = series.map((s: { [x: string]: any }) => s[dataPointIndex]);
          const indexedValues = allValues.map((val: any, index: any) => ({ index, val }));
          indexedValues.sort(
            (a: { val: number; index: number }, b: { val: number; index: number }) =>
              b.val - a.val || a.index - b.index
          );
          const rankMap = new Map<number, number>(); // index → rank
          indexedValues.forEach((item: { index: number }, rank: number) => {
            rankMap.set(item.index, rank + 1);
          });
          const rankOfValue = rankMap.get(seriesIndex)!;
          return `#${rankOfValue}: ` + f(symlog.invert(value), { decimal: 2 }) + '(USD)';
        },
      },
      x: {
        show: true,
        formatter: (value, { dataPointIndex, w }) => {
          return m_categories[dataPointIndex] ?? value;
        },
      },
    },
  });
  const chartOptions = useChart({
    colors: ['#2CA04E', '#E4B200', '#40A8D6', '#D9534F', '#D8BFD8'],
    legend: { show: true, showForSingleSeries: true },
    xaxis: {
      categories: chart.categories,
      labels: {
        formatter: (value, timestamp: number, opts: number) => {
          if (m_categories.length > 30) {
            const index = (chart.categories || []).indexOf(value);
            return index % 7 === 0 ? moment(new Date(value).toString()).format('DD-MM-YY') : '';
          }
          return moment(new Date(value).toString()).format('DD-MM-YY');
        },
        rotate: -45,
        rotateAlways: true,
      },
    },
    yaxis: [
      {
        labels: {
          formatter: (value: number) => fShortenNumber(value),
        },
        min: (min) => {
          if (min >= 0) return 0;
          // eslint-disable-next-line prefer-exponentiation-operator, no-restricted-properties
          const magnitude = Math.pow(10, Math.floor(Math.log10(-min)));
          const multiple = -min / magnitude; // example : 1.22
          const offset = multiple - Math.floor(multiple); // example : 0.22
          const buffer = offset < 0.5 ? 0.5 : 0;
          const bufferMagnitude = buffer * magnitude;
          const result = -(Math.round(multiple) * magnitude + bufferMagnitude);
          return result;
        },
      },
    ],
    tooltip: {
      y: {
        // formatter: (value: number) => f(value, { decimal: 2 }) + '(USD)',
        // title: {
        //   formatter: (seriesName: string) => {
        //     return seriesName.replace(/\s*\(.*?\)\s*/g, '');
        //   },
        // },
        formatter: (value: number, { series, seriesIndex, dataPointIndex }) => {
          const allValues = series.map((s: { [x: string]: any }) => s[dataPointIndex]);
          const indexedValues = allValues.map((val: any, index: any) => ({ index, val }));
          indexedValues.sort(
            (a: { val: number; index: number }, b: { val: number; index: number }) =>
              b.val - a.val || a.index - b.index
          );
          const rankMap = new Map<number, number>(); // index → rank
          indexedValues.forEach((item: { index: number }, rank: number) => {
            rankMap.set(item.index, rank + 1);
          });
          const rankOfValue = rankMap.get(seriesIndex)!;
          return `#${rankOfValue}: ` + f(value, { decimal: 2 }) + ' (USD)';
        },
      },
      x: {
        show: true,
        formatter: (value, { dataPointIndex, w }) => {
          return m_categories[dataPointIndex] ?? value;
        },
      },
    },
  });

  const filteredOptions = showLogScale ? logScaleChartOptions : chartOptions;

  return (
    <Card {...other} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CardHeader
        title={title}
        subheader={subheader}
        action={
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={showLogScale} onChange={handleToggleProfit} />}
              label={translate('module.dashBoard.chartMode')}
            />
          </Stack>
        }
      />
      <Chart
        type="line"
        series={filteredSeries}
        options={filteredOptions}
        minHeight={'250px'}
        height="100%"
        sx={{
          mb: 2,
          pl: 1,
          mt: 0,
          pr: 2,
          flex: 1,
          overflow: 'visible',
          '.apexcharts-canvas > svg': { overflow: 'visible' },
        }}
      />
    </Card>
  );
}
